import React, { useEffect, useCallback, useMemo, useContext } from 'react'
import { connect } from 'react-redux'
import { useAppDispatch } from 'hooks/reduxHooks'
import styled from 'styled-components'
import { rem } from 'polished'
import { replace } from 'connected-react-router'

import { getUrlOfferListFilters } from 'selectors/routerSelectors'
import HeadData from 'components/Common/HeadData'
import config from 'constants/config'

import PartnershipBanner from 'components/Common/Vendors/Partnership/PartnershipBanner'
import { LUXURY_ESCAPES } from 'constants/brands'

import { OFFER_TYPE_TOUR, OFFER_TYPE_TOUR_V2 } from 'constants/offer'
import * as InteractionStudioService from 'api/interactionStudio'

import Leaderboard from 'components/Common/Banner/Leaderboard'
import { BANNER_LOCATION_TOURS_AND_CRUISES } from 'constants/bannerLocations'
import { fetchBanners, fetchListBanners } from 'actions/ContentActions'
import { getPlacement } from 'lib/campaigns'
import Heading from 'components/Luxkit/Typography/Heading'
import OfferListFrontPageFilters from 'components/OfferList/OfferListFilter/OfferListFrontPageFilters'
import BodyText from 'components/Luxkit/Typography/BodyText'
import TextLink from 'components/Luxkit/TextLink'

import GlobalSearchHeroContainer from 'components/Search/GlobalSearch/GlobalSearchHeroContainer'
import { TOUR_V2_SEARCH_HERO_IMAGE_ID } from 'constants/tours'
import GlobalSearchContainerPane, { GlobalSearchContainerPaneContent } from 'components/Search/GlobalSearch/GlobalSearchContainerPane'
import TourV2SearchControls from 'components/Search/TourV2/TourV2SearchControls'
import GlobalSearchContextProvider from 'contexts/GlobalSearch/GlobalSearchContextProvider'
import { useGlobalToursSearchContext } from 'hooks/GlobalSearch/GlobalSearchVerticals/useGlobalToursSearch'
import { TOURS_OFFER_LIST_EXCLUDED_PARTNERSHIP_CODES } from 'constants/tours'
import OfferList, { OfferListV2Item } from 'components/OfferList/v2/OfferList'
import LayoutContainer from 'components/Common/LayoutContainer'
import GeoContext from 'contexts/geoContext'
import { OfferListEvents, OfferListEventsProvider } from 'components/OfferList/v2/OfferListEventsContext'
import GlobalMobileSearchHeroBar from 'components/SearchV2/Components/Mobile/GlobalMobileSearchHeroBar'
import { useTourV2SearchEnabled } from 'hooks/useTourV2SearchEnabled'
import tourV2VerticalNameSelector from 'selectors/TourV2/tourV2VerticalNameSelector'
import BannerCarousel from 'components/Common/Banner/BannerCarousel'
import { isStandaloneCruiseEnabled } from 'selectors/cruiseOfferSelectors'
import PromptCardSelector from 'components/Common/StickyPromptCard/PromptCardSelector'
import ErrorBoundary from 'components/Common/ErrorBoundary/ErrorBoundary'
import ToursPageHighIntentTours from './ToursPageHighIntentTours'
import AnalyticsPageContext, { AnalyticsPage } from 'contexts/Analytics/analyticsPageContext'
import { isManualSortingEnabledFor } from 'selectors/featuresSelectors'
import { useTranslation } from 'react-i18next'
import { EnabledFilters } from 'components/Search/type'
import TourV2TrendingDestinationsCarousel from 'components/OfferPage/TourV2/TourV2TrendingDestinationsCarousel'
import { getTourV2TrendingDestinations } from 'components/OfferPage/TourV2/TourV2TrendingLocationsList'
import TourUSPContainer from 'components/OfferPage/TourV2/TourUSPContainer'

const OffersContainer = styled.div`
  width: 100%;
  background-color: ${({ theme }) => theme.palette.neutral.eight};
`

const OfferListWrapper = styled(LayoutContainer)`
  padding-bottom: ${rem(16)};
  padding-top: ${rem(16)};

  > * + * {
    margin-top: ${rem(16)};
  }
`

const NoOffersMessage = styled.div`
  padding: ${rem(24)} 0;

  > * + * {
    margin-top: ${rem(8)};
  }
`

interface MappedStateProps {
  banners: Array<App.Banner>
  listBanners: Array<App.ListBanner>
  urlFilters: App.OfferListFilters
  toursVerticalName: string,
  standaloneCruiseEnabled: boolean,
}

interface Props { }

const filterTypes: Array<App.OfferListOfferType> = [OFFER_TYPE_TOUR, OFFER_TYPE_TOUR_V2]

const toursDestinations = ['Egypt', 'New York', 'Africa', 'Europe']

const enabledFilters: EnabledFilters = {
  holidayTypes: true,
  amenities: true,
  inclusions: true,
  locations: true,
  customerRating: false,
  priceFilter: false,
}

function ToursPage(props: Props & MappedStateProps) {
  const {
    urlFilters,
    banners,
    listBanners,
    toursVerticalName,
    standaloneCruiseEnabled,
  } = props
  const isManualSortingEnabled = isManualSortingEnabledFor('tours')
  const { currentRegionCode, partnership } = useContext(GeoContext)
  const isTourV2SearchEnabled = useTourV2SearchEnabled()

  // Up-front declaration of i18n namespaces used on this page, to ensure they're loaded
  // TODO: Figure out why we're using hotel-specific namespaces on this page
  useTranslation(['hotelOffer', 'offerList', 'hotelOfferList', 'tourOfferList'])

  const dispatch = useAppDispatch()

  useEffect(() => {
    dispatch(fetchBanners(BANNER_LOCATION_TOURS_AND_CRUISES))
    dispatch(fetchListBanners(BANNER_LOCATION_TOURS_AND_CRUISES))
  }, [dispatch])

  const resetFilters = useCallback(() => {
    dispatch(replace({}))
  }, [dispatch])

  const filters = useMemo((): App.OfferListFilters => {
    if (isManualSortingEnabled && urlFilters.locations?.length === 0) {
      return {
        ...urlFilters,
        offerTypes: [OFFER_TYPE_TOUR_V2],
        sortBy: 'vertical-tour',
        limit: 250,
      }
    }
    const finalFilters = {
      ...urlFilters,
      offerTypes: filterTypes,
      frontPageVisibleOnly: true,
    }
    if (standaloneCruiseEnabled) {
      finalFilters.holidayTypes = ['!Cruises']
    }
    return finalFilters
  }, [urlFilters, standaloneCruiseEnabled, isManualSortingEnabled])

  const offerListExtras: Array<OfferListV2Item> = useMemo(() => {
    const topBanner = getPlacement(banners, 'top')

    const extras: Array<OfferListV2Item> = [
      {
        element: <PartnershipBanner bannerId="types-page" />,
        show: !!(partnership && !TOURS_OFFER_LIST_EXCLUDED_PARTNERSHIP_CODES.has(partnership.code)),
        id: 'partnership',
        position: 'start',
      },
      {
        element: <Leaderboard banners={topBanner} />,
        show: !!topBanner.length,
        id: 'leaderboard',
        position: 1,
      },
      {
        id: 'list-p1',
        position: 2,
        element: <BannerCarousel banners={listBanners.slice(0, 3)} />,
        show: !!listBanners[0],
      },
      {
        id: 'tour-usp',
        position: 3,
        element: <TourUSPContainer />,
        show: true,
      },
      {
        id: 'list-p4',
        position: 4,
        element: <Leaderboard banners={[listBanners[3]]} />,
        show: !!listBanners[3],
      },
      {
        id: 'list-p8',
        position: 8,
        element: <Leaderboard banners={[listBanners[4]]} />,
        show: !!listBanners[4],
      },
      {
        id: 'list-p12',
        position: 12,
        element: <Leaderboard banners={[listBanners[5]]} />,
        show: !!listBanners[5],
      },
      {
        element: <NoOffersMessage>
          <Heading variant="heading3">No offers available</Heading>
          <BodyText variant="large">There are no offers matching your selected filters.</BodyText>
          <TextLink onClick={resetFilters}>Reset filters</TextLink>
        </NoOffersMessage>,
        id: 'no-offers-message',
        position: 2,
        show: 'hasNoOffers',
      }]
    return extras
  }, [banners, listBanners, partnership, resetFilters])

  const headTitle = config.BRAND === LUXURY_ESCAPES ?
    `Luxury ${toursVerticalName}` :
    `${toursVerticalName}`
  const description = config.BRAND === LUXURY_ESCAPES ?
    'Embark on a life changing tour or cruise. Curated by travel experts to save you time. Featuring luxury accommodation, select dining and exclusive inclusions.' :
    undefined

  const { globalToursSearchDispatch, globalToursSearchState } = useGlobalToursSearchContext()

  const onOfferListAnalyticEvent = useCallback((action: OfferListEvents, data: any) => {
    if (action === OfferListEvents.productClick) {
      const { offer, index } = data
      InteractionStudioService.trackOfferListClick({
        offerId: offer.id,
        index,
        list: 'homepage',
      })
    }
  }, [])

  return <AnalyticsPageContext.Provider value={AnalyticsPage.verticalPage} >
    <div>
      <HeadData
        title={`${headTitle} - ${config.title} ${currentRegionCode}`}
        description={description}
      />
      <ErrorBoundary fallback={null}>
        <PromptCardSelector abandonedCart hotLead tripReengagement />
      </ErrorBoundary>

      <GlobalSearchContextProvider
        state={globalToursSearchState}
        dispatch={globalToursSearchDispatch}
      >
        {isTourV2SearchEnabled && <GlobalSearchHeroContainer
          heroImageId={TOUR_V2_SEARCH_HERO_IMAGE_ID}
          title={toursVerticalName}
          gravity="north"
        >
          <GlobalSearchContainerPane>
            <GlobalSearchContainerPaneContent>
              <TourV2SearchControls />
            </GlobalSearchContainerPaneContent>
          </GlobalSearchContainerPane>
          <GlobalMobileSearchHeroBar placeholder="Search tours in" placeholderTextList={toursDestinations} />
        </GlobalSearchHeroContainer>}
        <ErrorBoundary fallback={null}>
          <ToursPageHighIntentTours />
        </ErrorBoundary>
        <OffersContainer>
          <OfferListWrapper>
            <TourV2TrendingDestinationsCarousel locations={getTourV2TrendingDestinations()}></TourV2TrendingDestinationsCarousel>
            <div>
              <OfferListFrontPageFilters
                types={filterTypes}
                existingFilters={filters}
                enabledFilters={enabledFilters}
              />
            </div>
            <OfferListEventsProvider onListEvent={onOfferListAnalyticEvent}>
              <OfferList
                filters={filters}
                interstitials={offerListExtras}
              />
            </OfferListEventsProvider>
          </OfferListWrapper>
        </OffersContainer>
      </GlobalSearchContextProvider>
    </div>
  </AnalyticsPageContext.Provider>
}

export default connect<MappedStateProps, void, Props, App.State>((state) => {
  return {
    listBanners: state.content.listBanners?.toursAndCruises ?? [],
    banners: state.content.banners.toursAndCruises,
    urlFilters: getUrlOfferListFilters(state),
    toursVerticalName: tourV2VerticalNameSelector(state),
    standaloneCruiseEnabled: isStandaloneCruiseEnabled(state),
  }
})(ToursPage)
